import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ServiceSearchKey } from 'src/app/shared/models/service';
import { Breadcrumbs } from '../../shared/models/breadcrumbs';
import {
  EntitlementRequest,
  EntitlementKey,
  Entitlement,
} from '../../shared/models/entitlement';
import { DataService } from '../../shared/services/data-service/data.service';
import { StoreService } from '../../shared/services/store-service/store.service';

@Component({
  selector: 'app-entitlement-service-client',
  templateUrl: './entitlement-service-client.component.html',
  styleUrls: [
    '../../app.component.css',
    './entitlement-service-client.component.scss',
  ],
})
export class EntitlementServiceClientComponent implements OnInit {
  serviceId: string;
  clientId: string;
  serviceName: string = '';
  clientName: string = '';
  entitlementServices: Entitlement[];
  entitlementKey: string[] = [];
  loading: boolean;
  searchString: string = '';
  editGroupId: string = '';
  updatingEntitlements: EntitlementRequest[] = [];
  showConfirmPopup: boolean;
  deleteGroupIds: string[] = [];
  confirmDeleteGroupId: string = '';
  pageMode: string = 'READ'; // Read/Edit
  breadcrumbItems: Breadcrumbs[];
  readonly MINIMUM_SEARCH_TERM_LENGTH: number = 3;
  selectedSearchKeyDescription: string = '';
  searchKey: string;
  clickedKey: ServiceSearchKey;
  searchKeyLoading: boolean = false;
  currentEntitlementData: EntitlementKey = {
    group: {},
    data: {},
    description: {},
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataService: DataService,
    public storeService: StoreService,
  ) {
    this.loading = true;
  }

  async ngOnInit(): Promise<void> {
    try {
      this.serviceId = this.route.snapshot.paramMap.get('serviceId') || '';
      this.clientId = this.route.snapshot.paramMap.get('clientId') || '';
      await this.dataService.initClients();
      await this.dataService.initServices();
      this.serviceName =
        this.storeService.services.find((a) => a.id == this.serviceId)?.name ||
        '';
      this.clientName =
        this.storeService.clients.find((a) => a.id == this.clientId)?.name ||
        '';

      await this.dataService.initEntitlementByServiceId(this.serviceId);
    } catch (error) {
      this.loading = false;
    }
    this.entitlementServices = this.storeService.entitlementServices.filter(
      (a) => a.clientId === this.clientId && a.serviceId === this.serviceId,
    );

    this.entitlementKey = [];
    this.storeService.services
      .filter((a) => a.id == this.serviceId)
      .forEach((service) => {
        service.serviceKeys
          .sort((a, b) => a.priority - b.priority)
          .forEach((key) => {
            this.entitlementKey.push(key.key);
          });
      });

    this.buildBreadcrumbs();
    this.loading = false;
  }

  buildBreadcrumbs() {
    this.breadcrumbItems = [
      {
        text: 'Service',
        link: '/service',
      },
      {
        text: this.serviceName,
        link: '/entitlement-service/' + this.serviceId,
      },
      {
        text: this.clientName,
        link:
          '/entitlement-service-client/' + this.clientId + '/' + this.serviceId,
      },
    ];
  }

  getDescriptionByKey(key: string) {
    return this.currentEntitlementData.description
      ? this.currentEntitlementData.description[key]
      : '';
  }

  async handleDelete(groupId: string) {
    this.deleteGroupIds.push(groupId);
    this.handleCancel();
    this.showConfirmPopup = false;

    let deleting: EntitlementRequest[] = [];
    this.entitlementKey.forEach((key) => {
      let entitlement = new EntitlementRequest();
      entitlement.clientId = this.clientId;
      entitlement.serviceId = this.serviceId;
      entitlement.groupId = groupId;
      entitlement.key = key;
      deleting.push(entitlement);
    });

    await this.dataService.deleteEntitlements(deleting);
    this.deleteGroupIds = this.deleteGroupIds.filter((a) => a !== groupId);
  }

  async handleSave() {
    this.pageMode = 'READ';
    this.editGroupId = '';
    let createdEntitlements = this.entitlementServices[0].entitlements.filter(
      (a) => a['group']['groupId'] === '0',
    );
    if (createdEntitlements.length > 0) {
      // Create
      let newGroupId =
        this.entitlementServices[0].entitlements.reduce(
          (element, max) =>
            element > Number(max['group']['groupId'])
              ? element
              : Number(max['group']['groupId']),
          0,
        ) + 1;
      let entitlements: EntitlementRequest[] = [];
      createdEntitlements.forEach((ent) => {
        this.entitlementKey.forEach((key) => {
          let entitlement = new EntitlementRequest();
          entitlement.clientId = this.clientId;
          entitlement.serviceId = this.serviceId;
          entitlement.key = key;
          entitlement.groupId = String(newGroupId);
          entitlement.value =
            ent['data'][key] ||
            (this.clickedKey !== undefined
              ? this.clickedKey.value
              : this.searchKey);
          entitlement.description = this.getDescriptionByKey(key);
          entitlements.push(entitlement);
          // exist edit mode
          ent['group']['groupId'] = String(newGroupId);
        });
      });
      await this.dataService.createEntitlements(entitlements);
    } else {
      // Update
      await this.dataService.updateEntitlements(this.updatingEntitlements);
    }
  }

  handleUpdate(groupId: string, key: string, value: string, event: any) {
    if (!event.target.value) return;
    const _description = this.getDescriptionByKey(key);

    this.entitlementServices[0].entitlements.forEach((ent) => {
      if (ent['group']['groupId'] === groupId) {
        ent['data'][key] = event.target.value;
        if (ent['description']) ent['description'][key] = _description;
      }
    });

    let updateItems = this.updatingEntitlements.filter(
      (a) =>
        a.clientId === this.clientId &&
        a.serviceId === this.serviceId &&
        a.key === key &&
        a.groupId === groupId,
    );
    if (updateItems.length > 0) {
      updateItems[0].value = event.target.value;
      updateItems[0].description = _description;
    } else {
      let entitlement = new EntitlementRequest();
      entitlement.clientId = this.clientId;
      entitlement.serviceId = this.serviceId;
      entitlement.key = key;
      entitlement.groupId = groupId;
      entitlement.value = event.target.value;
      entitlement.oldValue = value;
      entitlement.description = _description;
      this.updatingEntitlements.push(entitlement);
    }
  }

  addNewRecord() {
    this.handleCancel();
    // Case no any record
    if (this.entitlementServices.length === 0) {
      let entService = new Entitlement();
      entService.clientId = this.clientId;
      entService.serviceId = this.serviceId;
      entService.entitlements = [];
      this.storeService.entitlementServices.push(entService);
      this.entitlementServices = this.storeService.entitlementServices.filter(
        (a) => a.clientId === this.clientId && a.serviceId === this.serviceId,
      );
    }
    // add only one record per submit
    if (
      this.entitlementServices[0].entitlements.filter(
        (a) => a['group']['groupId'] === '0',
      ).length === 0
    ) {
      let entitlementKey: EntitlementKey = {
        data: {},
        group: {
          groupId: '0',
        },
        description: {},
      };

      this.entitlementKey.forEach((key, index) => {
        entitlementKey['data'][this.entitlementKey[index]] = '';
      });

      this.entitlementServices[0].entitlements.unshift(entitlementKey);
    }
    this.pageMode = 'EDIT';
  }

  handleEdit(groupId: string, event: any, currentEntitlementData: any) {
    this.handleCancel();
    this.currentEntitlementData = currentEntitlementData;
    this.editGroupId = groupId;
    this.updatingEntitlements = [];
    this.pageMode = 'EDIT';
  }

  handleCancel() {
    this.pageMode = 'READ';
    this.editGroupId = '';
    this.confirmDeleteGroupId = '';
    this.selectedSearchKeyDescription = '';
    this.entitlementServices.forEach((entService) => {
      entService.entitlements = entService.entitlements.filter(
        (grp) => grp['group']['groupId'] !== '0',
      );

      entService.entitlements.forEach((display) => {
        this.updatingEntitlements.forEach((editing) => {
          if (display['group']['groupId'] == editing.groupId) {
            display['data'][editing.key] = editing.oldValue;
          }
        });
      });
      this.updatingEntitlements = [];
    });
  }

  clearSearch() {
    this.searchString = '';
  }

  getMode(groupId: string) {
    if (groupId === '0' || groupId === this.editGroupId) return 'EDIT';
    else if (this.pageMode == 'READ') return 'READ';
    else return 'DISABLE';
  }

  isEditMode(groupId: string) {
    return groupId === '0' || groupId === this.editGroupId;
  }

  isReadMode(groupId: string) {
    return groupId === '0' || groupId === this.editGroupId;
  }

  openConfirmPopUp(groupId: string) {
    this.confirmDeleteGroupId = groupId;
    this.showConfirmPopup = true;
  }

  closePopUp(groupId: string) {
    this.showConfirmPopup = false;
    this.deleteGroupIds = this.deleteGroupIds.filter((a) => a !== groupId);
    this.handleCancel();
  }

  showDeletedBadge(groupId: string) {
    var searchIndex = this.deleteGroupIds.indexOf(groupId);
    if (searchIndex > -1) {
      return true;
    } else {
      return false;
    }
  }

  // Handles the event when the searchkey is clicked
  onEntitlementSearchKeyClick(groupId: string, key: string, value: string) {
    const tempEvent = {
      target: {
        value: this.clickedKey.value,
      },
    };
    this.currentEntitlementData.data[key] = this.clickedKey.value;
    if (this.currentEntitlementData.description)
      this.currentEntitlementData.description[key] =
        this.clickedKey.description;
    this.handleUpdate(groupId, key, value, tempEvent);
  }

  // Handles the event when the searchkey input value is changed
  onEntitlementSearchKeyChange(groupId: string, key: string, value: string) {
    const tempEvent = {
      target: {
        value: this.searchKey,
      },
    };
    this.handleUpdate(groupId, key, value, tempEvent);
  }
}
